import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { LOUNGE_CHANNEL_ID } from '@onr/schema'
import { captureException } from '@sentry/react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { getFirestore, writeBatch } from 'firebase/firestore'
import { compact, map, sortBy, uniq } from 'lodash-es'
import { nanoid } from 'nanoid'

import { isSessionError, useOnrApi } from '@/api/mutation/useOnrApi'
import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { toast } from '@/components/molecules/Toaster/useToast'
import { type TeamConversationViewProps } from '@/components/templates/TeamConversationView'
import { useRegisterSupportPage } from '@/hooks/useSupportContext'
import { DashboardAlertContext } from '@/model/contexts/DashboardAlertContext'
import { collections } from '@/schema'
import { uploadVoiceClip } from '@/state/firebase-storage/uploadVoiceClip'
import { regionQuery } from '@/state/regions/region'
import { teamMessagesQuery } from '@/state/teamMessages/teamMessages'
import { dashboardRegionTeamQuery } from '@/state/teams/teams'
import { transportRequestsQuery } from '@/state/transportRequests/transportRequests'

import { teamCarType } from '../dashboard/data/teamCar'

import { messageTeamStatus, teamStatus } from './teamStatus'
import { useMessagingRail } from './useMessagingRail'

export const useTeamMessagesPage = (): TeamConversationViewProps => {
  const { regionId, teamId } = useParams({ from: '/dashboard/$regionId/messages/$teamId' })
  const user = useAuthenticatedFirebaseUser()
  const intl = useIntl()
  const region = useSuspenseQuery(regionQuery(regionId))
  const { data: teams } = useSuspenseQuery(dashboardRegionTeamQuery(region.data.data))
  const { data: messages } = useSuspenseQuery(teamMessagesQuery(teamId))
  const transportRequestIds = useMemo(() => uniq(compact(map(messages.data, 'transportRequestId'))), [messages.data])
  const { data: transportRequests } = useQuery(transportRequestsQuery(transportRequestIds))
  const { alerts: contextAlerts, dismissAlert } = useContext(DashboardAlertContext)

  const broadcastMutation = useOnrApi<
    { successCount: number; failureCount: number; totalTeams: number },
    { regionId: string; message: string } | { regionId: string; audio: { storagePath: string; durationMs: number } }
  >({
    type: 'sendBroadcastMessage',
  })

  useEffect(() => {
    contextAlerts
      .filter((alert) => alert.type === 'teamMessage' && alert.teamId === teamId && !alert.isDismissed)
      .forEach((alert) => {
        dismissAlert(alert._id)
      })
  }, [contextAlerts, teamId, dismissAlert])

  const teamItems = useMemo(
    () =>
      sortBy(
        teams.data.map((team) => ({
          _id: team._id,
          hasUnreadMessages: team.messagingStatus.hasUnreadTeamMessages,
          carNumber: String(team.number ?? '?'),
          carType: teamCarType(team.vehicle),
          state: teamStatus(team),
          count: team.messagingStatus.teamMessageCount,
        })),
        (team) => Number(team.carNumber) || Infinity
      ),
    [teams.data]
  )

  const selectedTeam = useMemo(() => teamItems.find((team) => team._id === teamId), [teamItems, teamId])

  useRegisterSupportPage({
    detail: selectedTeam
      ? intl.formatMessage({ id: 'soutien.page.object.team' }, { number: selectedTeam.carNumber })
      : null,
    object: selectedTeam ? { label: selectedTeam.carNumber, url: globalThis.location.href } : null,
  })

  const rail = useMessagingRail({
    regionId,
    eveningId: region.data.data?.currentOperatingDate?.eveningId,
    teamsHaveUnread: teamItems.some((team) => team.hasUnreadMessages),
    destination: 'teams',
    teamsTo: '/dashboard/$regionId/messages',
    teamsParams: { regionId },
    channelId: LOUNGE_CHANNEL_ID,
    channelTo: '/dashboard/$regionId/messages/channels/$channelId',
    channelParams: { regionId, channelId: LOUNGE_CHANNEL_ID },
  })

  const messageItems = useMemo(
    () =>
      messages.data.map((message) => {
        const transportRequest = transportRequests?.data?.find((tr) => tr._id === message.transportRequestId)
        return {
          _id: message._id,
          sender: message.sender,
          senderName: message.senderName,
          volunteerRole: message.volunteerRole,
          createdAt: message.createdAt,
          readAt: message.readAt,
          text: message.text,
          firebaseStoragePath: message.firebaseStoragePath,
          imageHeight: message.imageProperties?.height,
          imageWidth: message.imageProperties?.width,
          audioStoragePath: message.audioStoragePath,
          audioDurationMs: message.audioDurationMs,
          isBroadcast: message.isBroadcast,
          car: selectedTeam && {
            state: messageTeamStatus(message),
            carNumber: selectedTeam.carNumber,
            carType: selectedTeam.carType,
          },
          transportRequest: transportRequest && {
            _id: transportRequest._id,
            piloteId: String(transportRequest.piloteId),
            status: message.transportRequestStatus,
            address:
              message.transportRequestStatus === 'allocated' || message.transportRequestStatus === 'assigned'
                ? transportRequest.origin?.formattedAddress
                : message.transportRequestStatus === 'on_board'
                  ? transportRequest.destination?.formattedAddress
                  : '',
          },
        }
      }),
    [messages.data, transportRequests?.data, selectedTeam]
  )

  const actions = useMemo(() => {
    if (!selectedTeam) {
      return {}
    }

    if (selectedTeam?.hasUnreadMessages) {
      return {
        onConfirmAsRead: async () => {
          const batch = writeBatch(getFirestore())

          batch.update(collections.teams.write.doc(selectedTeam._id), {
            'messagingStatus.hasUnreadTeamMessages': false,
          })

          messages.data.forEach((message) => {
            if (message.sender === 'volunteer' && !message.readAt) {
              batch.update(collections.teams(selectedTeam._id).messages.write.doc(message._id), { readAt: new Date() })
            }
          })

          await batch.commit()
        },
      }
    }

    return {
      onSendMessage: async (message: string) => {
        await collections.teams(selectedTeam._id).messages.add({
          sender: 'admin',
          senderId: user.firebaseUser.uid,
          text: message,
          createdAt: new Date(),
        })
      },
      onSendVoiceMessage: async (blob: Blob, durationMs: number) => {
        const audioStoragePath = await uploadVoiceClip(`teams/${selectedTeam._id}/audio/${nanoid()}.m4a`, blob)
        // The clip is uploaded, so the write is queued locally and reaches the conversation at once; its promise only
        // settles on the server's acknowledgement, which a dropped connection delays.
        collections
          .teams(selectedTeam._id)
          .messages.add({
            sender: 'admin',
            senderId: user.firebaseUser.uid,
            audioStoragePath,
            audioDurationMs: durationMs,
            createdAt: new Date(),
          })
          .catch((error: unknown) => {
            captureException(error, { tags: { 'team.message': 'voice' } })
            toast({
              title: intl.formatMessage({ id: 'toast.broadcast.error.title' }),
              description: intl.formatMessage({ id: 'message.voice.sendFailed' }),
              variant: 'destructive',
            })
          })
      },
    }
  }, [selectedTeam, messages.data, user.firebaseUser.uid, intl])

  const eveningId = region.data.data?.currentOperatingDate?.eveningId

  const toastBroadcastSuccess = (result: { successCount: number; totalTeams: number }) => {
    toast({
      title: intl.formatMessage({ id: 'toast.broadcast.success.title' }),
      description: intl.formatMessage(
        { id: 'toast.broadcast.success.description' },
        { count: result.successCount, total: result.totalTeams }
      ),
      variant: 'default',
    })
  }

  const toastBroadcastError = (error: unknown) => {
    if (isSessionError(error)) {
      return
    }
    toast({
      title: intl.formatMessage({ id: 'toast.broadcast.error.title' }),
      description: intl.formatMessage({ id: 'toast.broadcast.error.description' }),
      variant: 'destructive',
    })
  }

  return {
    rail,
    messages: messageItems,
    teams: teamItems,
    selectedTeam: teamId,
    regionName: region.data.data?.name ?? '',
    piloteRegionId: region.data.data?.piloteId ?? 0,
    onBroadcastMessage: async (message: string) => {
      try {
        toastBroadcastSuccess(await broadcastMutation.mutateAsync({ regionId, message }))
      } catch (error) {
        toastBroadcastError(error)
      }
    },
    onBroadcastVoiceMessage: async (blob: Blob, durationMs: number) => {
      try {
        if (eveningId === undefined) {
          throw new Error(`No current operating evening for region ${regionId}`)
        }
        const storagePath = await uploadVoiceClip(`regions/${regionId}/broadcasts/${eveningId}/${nanoid()}.m4a`, blob)
        toastBroadcastSuccess(await broadcastMutation.mutateAsync({ regionId, audio: { storagePath, durationMs } }))
      } catch (error) {
        toastBroadcastError(error)
        throw error
      }
    },
    ...actions,
  }
}
